import { useState } from "react";
import { type Attachment, ApiError } from "./api";
import { shrink } from "./pos/photos";
import { fileForReading } from "./reading";
import { CROP_MARGIN } from "./certCover";
import { Alert, Working } from "./ui";

/**
 * The paper behind a bank line or a supplier purchase: the cheque, the supplier's invoice
 * (D, 5 October 2026). A photo is made smaller before it goes, as the booth's photos are,
 * but kept large enough to read an invoice off; a PDF goes as it is.
 */

/** Long enough to read the small print on an invoice. */
const PAPER_SIDE = 2400;

/** A picked file, ready to send. */
export interface PickedFile {
  readonly contentType: string;
  readonly data: string;
}

export async function prepareFile(file: File): Promise<PickedFile> {
  if (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
    const read = await fileForReading(file);
    return { contentType: "application/pdf", data: read.data };
  }
  const photo = await shrink(file, PAPER_SIDE);
  URL.revokeObjectURL(photo.preview);
  return { contentType: photo.contentType, data: photo.data };
}

/**
 * A slab's photo cut to the slab, with a little room round it (D, 9 October 2026), using
 * where the reading said the slab is. With no box, or anything odd, the photo as taken.
 */
export async function cropToSlab(
  file: File,
  box: { left: number; top: number; right: number; bottom: number } | null | undefined,
): Promise<PickedFile> {
  if (!box) return prepareFile(file);
  try {
    const image = await createImageBitmap(file);
    const margin = CROP_MARGIN;
    const x = Math.max(0, (box.left - margin) * image.width);
    const y = Math.max(0, (box.top - margin) * image.height);
    const width = Math.min(image.width, (box.right + margin) * image.width) - x;
    const height = Math.min(image.height, (box.bottom + margin) * image.height) - y;
    const scale = Math.min(1, PAPER_SIDE / Math.max(width, height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const context = canvas.getContext("2d");
    if (!context) throw new Error("no canvas");
    context.drawImage(image, x, y, width, height, 0, 0, canvas.width, canvas.height);
    image.close();
    const data = canvas.toDataURL("image/jpeg", 0.85).split(",")[1] ?? "";
    if (!data) throw new Error("no photo");
    return { contentType: "image/jpeg", data };
  } catch {
    return prepareFile(file);
  }
}

/** The files on one record, each opening in a new tab, with a way to add and take off. */
export function Attachments({
  id,
  items,
  onAdd,
  onRemove,
}: {
  readonly id: string;
  readonly items: readonly Attachment[];
  readonly onAdd: (file: PickedFile) => Promise<void>;
  readonly onRemove: (attachment: Attachment) => Promise<void>;
}): React.ReactElement {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function run(work: () => Promise<void>): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      await work();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "The file could not be saved");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {items.length > 0 ? (
        <div className="flex flex-wrap gap-3 mb-3">
          {items.map((item, index) => (
            <div key={item.id} className="relative">
              <a
                href={`/api/photos/${item.id}`}
                target="_blank"
                rel="noreferrer"
                className="block w-[5.5rem] h-[5.5rem] rounded-md border border-defaultborder overflow-hidden bg-light"
                title="Open"
              >
                {item.contentType === "application/pdf" ? (
                  <span className="flex flex-col items-center justify-center h-full text-[0.75rem]">
                    <i className="ri-file-pdf-2-line text-[1.75rem] text-danger" />
                    PDF {index + 1}
                  </span>
                ) : (
                  <img src={`/api/photos/${item.id}`} alt={`File ${index + 1}`} className="w-full h-full object-cover" />
                )}
              </a>
              <button
                type="button"
                className="absolute -top-2 -end-2 w-6 h-6 rounded-full bg-white dark:bg-bodybg border border-defaultborder text-[0.75rem] leading-none"
                aria-label="Remove this file"
                title="Remove"
                disabled={busy}
                onClick={() => {
                  if (window.confirm("Remove this file?")) void run(() => onRemove(item));
                }}
              >
                <i className="ri-close-line" />
              </button>
            </div>
          ))}
        </div>
      ) : null}
      <label htmlFor={id} className={`ti-btn ti-btn-light !mb-0 ${busy ? "pointer-events-none opacity-60" : ""}`}>
        {busy ? (
          <Working label="Uploading" />
        ) : (
          <>
            <i className="ri-attachment-2" /> Attach photo or PDF
          </>
        )}
      </label>
      <input
        id={id}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(changed) => {
          const file = changed.target.files?.[0];
          changed.target.value = "";
          if (file) void run(async () => onAdd(await prepareFile(file)));
        }}
      />
    </div>
  );
}
